<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import routerpath from '../../router/routerpath';
import { useMainStore } from '../../stores/index';
import Cookie from 'js-cookie';
import SidebarTooltip from './SidebarTooltip.vue';

const Store = useMainStore();
Store.Token();
const route = useRoute();

const activeTab = ref('');
const Loading = ref(true);
const external = ref(false);
const systemNow = ref('');

const menuSections = [
  {
    id: 'documentos',
    title: 'Documentos',
    items: [
      { name: 'Home', label: 'Início', icon: ['fas', 'house-chimney'], routeName: 'Home' },
      { name: 'Recebidos', label: 'Recebidos', icon: ['fas', 'inbox'], routeName: 'Recebidos', badgeKey: 'NotViewedTrue' },
      { name: 'Enviados', label: 'Enviados', icon: ['fas', 'paper-plane'], routeName: 'Enviados' },
      { name: 'Assinaturas', label: 'Assinaturas', icon: ['fas', 'pen-clip'], routeName: 'Assinaturas', badgeKey: 'DocLessSignTrue' },
      { name: 'Favoritos', label: 'Favoritos', icon: ['fas', 'star'], routeName: 'Favoritos' },
      { name: 'Todos', label: 'Todos os documentos', icon: ['fas', 'file'], routeName: 'Todos os documentos' },
      { name: 'Rascunhos', altMatch: 'Models', label: 'Rascunhos', icon: ['fas', 'pen-to-square'], routeName: 'Rascunhos' }
    ]
  },
  {
    id: 'gerenciamento',
    title: 'Gerenciamento',
    items: [
      { name: 'Setores', label: 'Setores', icon: ['fas', 'id-badge'], routeName: 'Setores' },
      { name: 'Servidores', label: 'Servidores', icon: ['fas', 'user-group'], routeName: 'Servidores', flip: 'horizontal' }
    ]
  },
  {
    id: 'suporte',
    items: [
      { name: 'Suporte', label: 'Suporte', icon: ['fas', 'phone'], routeName: 'Suporte' }
    ]
  }
];

const isCollapsed = computed(() => {
  if (Store.mobile) {
    return false;
  }
  return !Store.isPinned;
});

const updateActiveTab = () => {
  const routeArr = route.path.split(routerpath + '/');
  activeTab.value = routeArr[1] || '';
};

const setActiveTab = (tabName) => {
  activeTab.value = tabName;
};

const isItemActive = (item) => {
  if (!activeTab.value) {
    return item.name === 'Home' && route.name === 'Home';
  }
  if (route.path === systemNow.value + '/') {
    return false;
  }
  if (item.name === 'Home') {
    return activeTab.value === 'Home' || activeTab.value === '';
  }
  if (route.path === systemNow.value + '/Home') {
    return false;
  }

  const currentFirstSegment = activeTab.value.split('/')[0];
  if (currentFirstSegment === item.name) {
    return true;
  }
  if (item.altMatch && currentFirstSegment === item.altMatch) {
    return true;
  }
  if (route.name === item.routeName) {
    return true;
  }
  return false;
};

const handleItemClick = (e, navigate, item) => {
  setActiveTab(item.name);
  if (Store.mobile) {
    Store.isPinned = false;
  }
  if (navigate) {
    navigate(e);
  }
};

const handleCreateDocument = () => {
  resetCreate();
  setActiveTab('Criar de documento');
  if (Store.mobile) {
    Store.isPinned = false;
  }
};

const getBadgeCount = (badgeKey) => {
  if (!badgeKey) return 0;
  return Number(Store[badgeKey]) || 0;
};

const toggleCollapse = () => {
  Store.toggleConfigurator();
  Store.isPinned = !Store.isPinned;
};

const resetCreate = () => {
  Store.ResetCreateDocument();
};

const logout = () => {
  Cookie.remove('User');
  Cookie.remove('Session_ID');
  location.reload();
};

watch(() => route.path, () => {
  updateActiveTab();
});

onMounted(() => {
  systemNow.value = '/' + routerpath;
  updateActiveTab();
  
  const OnSystem = setInterval(() => {
    if (route.name !== null && route.name !== undefined && route.name !== 'Login') {
      if (route.path !== '/' + routerpath + '/') {
        clearInterval(OnSystem);
        updateActiveTab();
      }
    }
  }, 500);

  const OnSystemUser = setInterval(() => {
    if (Store.user && Store.user.id !== undefined) {
      clearInterval(OnSystemUser);
      if (Store.user.abilities) {
        Store.user.abilities.forEach(abilitie => {
          if (abilitie.id === 5) {
            external.value = true;
          }
        });
      }
      Loading.value = false;
    }
  }, 500);
});
</script>

<template>
  <div class="sidebar-wrapper d-flex flex-column h-100" :class="{ 'collapsed': isCollapsed }">
    <div class="sidebar-top-action" :class="isCollapsed ? 'px-2 pt-3 pb-2 text-center' : 'px-3 pt-3 pb-2'">
      <SidebarTooltip text="Criar documento" :disabled="!isCollapsed">
        <router-link
          :to="{ name: 'Criar de documento' }"
          class="btn-create-doc d-flex align-items-center"
          :class="{ 'btn-create-collapsed': isCollapsed }"
          @click="handleCreateDocument"
        >
          <font-awesome-icon :icon="['fas', 'plus']" class="btn-create-icon" />
          <span v-if="!isCollapsed" class="btn-create-text ms-2 font-docs">Criar documento</span>
        </router-link>
      </SidebarTooltip>
    </div>

    <div class="sidebar-divider mx-3"></div>

    <div class="sidebar-content flex-grow-1" :style="isCollapsed ? 'overflow: visible !important' : 'overflow-y: auto'">
      <div v-if="Loading" class="p-3 text-center">
        <span class="text-white-50 font-docs" style="font-size: 13px;">Carregando...</span>
      </div>
      <div v-else-if="!external" class="sidebar-nav-container">
        
        <div v-for="(section, sIndex) in menuSections" :key="section.id || sIndex" class="sidebar-section">
          <div v-if="section.title && !isCollapsed" class="sidebar-section-header px-3 pb-1">
            <span class="sidebar-section-title font-docs">{{ section.title }}</span>
          </div>

          <ul class="nav flex-column sidebar-nav-list" :class="isCollapsed ? 'px-2' : 'px-2'">
            <li
              v-for="item in section.items"
              :key="item.name"
              class="sidebar-nav-item mb-1"
            >
              <SidebarTooltip :text="item.label" :disabled="!isCollapsed">
                <router-link
                  :to="{ name: item.routeName }"
                  custom
                  v-slot="{ href, navigate }"
                >
                  <a
                    :href="href"
                    @click="handleItemClick($event, navigate, item)"
                    class="sidebar-nav-link d-flex align-items-center position-relative"
                    :class="{
                      'active': isItemActive(item),
                      'justify-content-center': isCollapsed,
                      'px-3': !isCollapsed
                    }"
                  >
                    <span class="sidebar-icon-wrapper d-flex align-items-center justify-content-center position-relative">
                      <font-awesome-icon
                        :icon="item.icon"
                        :flip="item.flip"
                        class="sidebar-icon"
                      />
                      <span
                        v-if="isCollapsed && item.badgeKey && getBadgeCount(item.badgeKey) > 0"
                        class="sidebar-badge-dot"
                      ></span>
                    </span>
                    
                    <span v-if="!isCollapsed" class="sidebar-label font-docs flex-grow-1 ms-3 text-truncate">
                      {{ item.label }}
                    </span>

                    <span
                      v-if="!isCollapsed && item.badgeKey && getBadgeCount(item.badgeKey) > 0"
                      class="sidebar-badge badge rounded-pill font-docs"
                    >
                      {{ getBadgeCount(item.badgeKey) }}
                    </span>
                  </a>
                </router-link>
              </SidebarTooltip>
            </li>
          </ul>

          <div v-if="sIndex < menuSections.length - 1" class="sidebar-divider mx-3"></div>
        </div>

        <div v-if="Store.mobile" class="sidebar-section mt-1">
          <ul class="nav flex-column sidebar-nav-list px-2">
            <li class="sidebar-nav-item mb-1">
              <router-link :to="{ name: 'profile' }" custom v-slot="{ href, navigate }">
                <a
                  :href="href"
                  @click="handleItemClick($event, navigate, { name: 'profile' })"
                  class="sidebar-nav-link d-flex align-items-center px-3"
                  :class="{ 'active': activeTab === 'profile' }"
                >
                  <span class="sidebar-icon-wrapper d-flex align-items-center justify-content-center">
                    <font-awesome-icon :icon="['fas', 'user']" class="sidebar-icon" />
                  </span>
                  <span class="sidebar-label font-docs ms-3">Meu perfil</span>
                </a>
              </router-link>
            </li>
          </ul>
        </div>

      </div>
    </div>

    <div class="sidebar-footer p-2">
      <SidebarTooltip text="Expandir" :disabled="!isCollapsed">
        <button
          v-if="!Store.mobile"
          type="button"
          class="btn-sidebar-toggle d-flex align-items-center w-100"
          :class="{ 'justify-content-center': isCollapsed, 'px-3': !isCollapsed }"
          @click="toggleCollapse"
        >
          <font-awesome-icon
            :icon="['fas', isCollapsed ? 'chevron-right' : 'chevron-left']"
            class="toggle-icon"
          />
          <span v-if="!isCollapsed" class="toggle-text ms-2 font-docs">Recolher</span>
        </button>
      </SidebarTooltip>

      <button
        v-if="Store.mobile"
        type="button"
        class="btn-sidebar-logout d-flex align-items-center px-3 py-2 w-100"
        @click="logout"
      >
        <img src="../../assets/img/logoutred.png" class="logout-icon" alt="Sair" />
        <span class="logout-text ms-2 font-docs">Sair</span>
      </button>
    </div>
  </div>
</template>

<style scoped>
.sidebar-wrapper {
  background-color: transparent;
  width: 100%;
  height: 100%;
  user-select: none;
  display: flex;
  flex-direction: column;
  overflow: visible !important;
}

.sidebar-wrapper.collapsed {
  overflow: visible !important;
}

.btn-create-doc {
  background-color: #F1860B;
  color: #FFFFFF !important;
  border-radius: 8px;
  height: clamp(36px, 5vh, 42px);
  width: 100%;
  padding: 0 14px;
  text-decoration: none;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.1s ease;
  justify-content: center;
}

.btn-create-doc:hover {
  background-color: #d97405;
  color: #FFFFFF !important;
}

.btn-create-doc:active {
  transform: scale(0.98);
}

.btn-create-collapsed {
  width: clamp(38px, 5vh, 44px) !important;
  height: clamp(38px, 5vh, 44px) !important;
  padding: 0 !important;
  margin: 0 auto;
  border-radius: 8px;
  justify-content: center !important;
}

.btn-create-icon {
  font-size: 15px;
}

.btn-create-text {
  font-size: clamp(12px, 1.6vh, 14px);
  white-space: nowrap;
}

.sidebar-section-header {
  margin-top: 2px;
}

.sidebar-section-title {
  color: #E8E8E8;
  font-size: clamp(11px, 1.4vh, 13px);
  font-weight: 500;
  letter-spacing: 0.3px;
  font-family: "Docs Light", sans-serif;
}

.sidebar-nav-list {
  list-style: none;
  padding-left: 0;
  margin-bottom: 0;
}

.sidebar-nav-item {
  width: 100%;
  margin-bottom: clamp(2px, 0.8vh, 4px);
}

.sidebar-nav-link {
  height: clamp(34px, 4.5vh, 42px);
  width: 100%;
  border-radius: 10px;
  color: #FFFFFF !important;
  text-decoration: none;
  transition: all 0.2s ease;
  cursor: pointer;
}

.sidebar-nav-link:hover {
  background-color: rgba(255, 255, 255, 0.08);
  color: #FFFFFF !important;
}

.sidebar-nav-link.active {
  background-color: #FFFFFF !important;
  color: #1e1e1e !important;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);
}

.sidebar-nav-link.active .sidebar-icon {
  color: #1e1e1e !important;
}

.sidebar-nav-link.active .sidebar-label {
  color: #1e1e1e !important;
  font-weight: 600;
}

.collapsed .sidebar-nav-link {
  width: clamp(38px, 5vh, 44px);
  height: clamp(38px, 5vh, 44px);
  margin: 0 auto;
  border-radius: 12px;
}

.collapsed .sidebar-nav-link.active {
  background-color: #FFFFFF !important;
}

.sidebar-icon-wrapper {
  width: 22px;
  height: 22px;
  flex-shrink: 0;
}

.sidebar-icon {
  font-size: clamp(14px, 1.8vh, 16px);
  color: #FFFFFF;
  transition: color 0.2s ease;
}

.sidebar-label {
  font-family: "Docs Light", sans-serif;
  font-size: clamp(12px, 1.6vh, 14px);
  color: #FFFFFF;
  letter-spacing: 0.2px;
}

.sidebar-badge {
  background-color: #0DCAF0 !important;
  color: #000000 !important;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 7px;
  line-height: 1.2;
}

.sidebar-badge-dot {
  position: absolute;
  top: -2px;
  right: -4px;
  width: 8px;
  height: 8px;
  background-color: #0DCAF0;
  border-radius: 50%;
  border: 1.5px solid #363534;
}

.sidebar-divider {
  height: 1px;
  background-color: rgba(255, 255, 255, 0.568);
  margin-top: clamp(6px, 1vh, 12px);
  margin-bottom: 2px;
}

.sidebar-footer {
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  margin-top: auto;
}

.btn-sidebar-toggle {
  background: transparent;
  border: none;
  color: #E8E8E8;
  height: 38px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-sidebar-toggle:hover {
  color: #FFFFFF;
  background-color: rgba(255, 255, 255, 0.05);
}

.toggle-icon {
  font-size: 13px;
}

.toggle-text {
  font-size: 13px;
  font-weight: 400;
}

.btn-sidebar-logout {
  background: transparent;
  border: none;
  cursor: pointer;
}

.logout-icon {
  width: 14px;
  height: 14px;
}

.logout-text {
  color: #EA868F;
  font-size: 15px;
  font-weight: 500;
}

.sidebar-content {
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.15) transparent;
  overflow-y: auto;
  overflow-x: visible !important;
  flex-grow: 1;
}

.sidebar-content::-webkit-scrollbar {
  width: 4px;
}

.sidebar-content::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.15);
  border-radius: 4px;
}
</style>